// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — useFlowSurface(weight, cfg): pick the right in-context surface for
// an interaction by its "weight" and the current viewport, then open it on the
// LayeredModalHost stack. On mobile every weight collapses to a bottom-sheet so
// the stack behaves as a navigation back-stack.

import * as React from "react";

import { useLayeredModal } from "./LayeredModalHost";
import type { LayerApi, SurfaceKind, SurfaceOptions } from "./LayeredModalHost";
import { useIsMobile } from "./useIsMobile";

/** Interaction weight: how disruptive / how much surface the flow needs. */
export type FlowWeight = "light" | "medium" | "heavy";

export interface ResolveSurfaceContext {
  isMobile: boolean;
}

/**
 * Pure mapping (weight, viewport) → surface kind. Exported for unit tests.
 * - mobile: always a bottom-sheet (back-stack)
 * - light (peek/quick edit): side-sheet on desktop
 * - medium (form/picker): modal on desktop
 * - heavy (multi-step workflow): modal on desktop
 */
export function resolveSurfaceKind(weight: FlowWeight, ctx: ResolveSurfaceContext): SurfaceKind {
  if (ctx.isMobile) return "bottom-sheet";
  switch (weight) {
    case "light":
      return "sheet";
    case "medium":
    case "heavy":
    default:
      return "modal";
  }
}

export interface FlowSurfaceConfig extends Omit<SurfaceOptions, "kind"> {
  /** Force a specific kind regardless of weight (rarely needed). */
  kind?: SurfaceKind;
  mobileBreakpoint?: number;
}

export interface UseFlowSurfaceResult {
  /** The kind that would be used for the current viewport. */
  kind: SurfaceKind;
  isMobile: boolean;
  /** Open a layer of the resolved kind; resolves with the layer's result. */
  open<T = unknown>(render: (api: LayerApi<T>) => React.ReactNode): Promise<T | undefined>;
}

/**
 * Open in-context surfaces sized to the interaction. Must be used within a
 * <LayeredModalHost>.
 */
export function useFlowSurface(
  weight: FlowWeight,
  cfg: FlowSurfaceConfig = {},
): UseFlowSurfaceResult {
  const modal = useLayeredModal();
  const isMobile = useIsMobile(cfg.mobileBreakpoint ?? 640);
  const kind = cfg.kind ?? resolveSurfaceKind(weight, { isMobile });

  const open = React.useCallback(
    <T>(render: (api: LayerApi<T>) => React.ReactNode) =>
      modal.open<T>(render, {
        kind,
        title: cfg.title,
        className: cfg.className,
        dismissable: cfg.dismissable,
      }),
    [modal, kind, cfg.title, cfg.className, cfg.dismissable],
  );

  return { kind, isMobile, open };
}
